import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueries } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  getLocationBodies,
  getLocationReports,
  getOneOffStops,
  getRouteAssignments,
  getServiceLocation,
  getUpcomingServices,
  deleteServiceLocation,
  updateServiceLocation,
} from '@/features/service-locations/api/serviceLocations';
import { PoolManager } from '@/features/service-locations/components/PoolManager';
import { ScheduleManager } from '@/features/service-locations/components/ScheduleManager';
export function ServiceLocationPage() {
  const clientId = Number(useParams().clientId);
  const locationId = Number(useParams().locationId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get('tab') === 'schedule' ? 3 : 0);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [name, setName] = useState('');
  const [gateCode, setGateCode] = useState('');
  const results = useQueries({
    queries: [
      {
        queryKey: ['location', clientId, locationId],
        queryFn: () => getServiceLocation(clientId, locationId),
      },
      {
        queryKey: ['location', clientId, locationId, 'bodies'],
        queryFn: () => getLocationBodies(clientId, locationId),
      },
      {
        queryKey: ['location', clientId, locationId, 'reports'],
        queryFn: () => getLocationReports(clientId, locationId),
      },
      {
        queryKey: ['location', clientId, locationId, 'assignments'],
        queryFn: () => getRouteAssignments(clientId, locationId),
      },
      {
        queryKey: ['location', clientId, locationId, 'one-off-stops'],
        queryFn: () => getOneOffStops(clientId, locationId),
      },
      {
        queryKey: ['location', clientId, locationId, 'upcoming'],
        queryFn: () => getUpcomingServices(clientId, locationId),
      },
    ],
  });
  const [locationQuery, bodiesQuery, reportsQuery, assignmentsQuery, oneOffQuery, upcomingQuery] =
    results;
  const saveLocation = useMutation({
    mutationFn: () => updateServiceLocation(clientId, locationId, name.trim(), gateCode.trim()),
    onSuccess: async () => {
      setEditing(false);
      await locationQuery.refetch();
    },
  });
  const removeLocation = useMutation({
    mutationFn: () => deleteServiceLocation(clientId, locationId),
    onSuccess: () => navigate(`/clients/${clientId}`),
  });
  const location = locationQuery.data;
  const address = location?.address;
  const addressText = [
    address?.street_number,
    address?.route,
    address?.locality,
    address?.postal_code,
  ]
    .filter(Boolean)
    .join(' ');
  if (locationQuery.isError)
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        This service location could not be loaded.
      </Alert>
    );
  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" sx={{ minHeight: 44, px: 1 }}>
          <Button component={Link} to={`/clients/${clientId}`} startIcon={<ArrowBackIcon />}>
            Back
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void Promise.all(results.map((x) => x.refetch()))}
          >
            Refresh
          </Button>
          {location && !editing && (
            <Button
              startIcon={<EditOutlinedIcon />}
              onClick={() => {
                setName(location.name);
                setGateCode(location.gate_code ?? '');
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
          {editing && (
            <Button
              disabled={!name.trim() || saveLocation.isPending}
              onClick={() => saveLocation.mutate()}
            >
              Save
            </Button>
          )}
          {editing && <Button onClick={() => setEditing(false)}>Cancel</Button>}
          {location && (
            <Button
              color="error"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
          {location && (
            <Button
              component="a"
              target="_blank"
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressText || location.name)}`}
              startIcon={<DirectionsOutlinedIcon />}
            >
              Directions
            </Button>
          )}
        </Stack>
      </Paper>
      <Paper elevation={0} sx={{ ...paperSx, mt: 1 }}>
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Typography variant="h1">{location?.name ?? 'Loading service location…'}</Typography>
          <Typography color="text.secondary">
            {addressText || 'Address not provided'} · Gate code: {location?.gate_code || '—'}
          </Typography>
          <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mt: 1 }}>
            <Tab label="Summary" />
            <Tab label={`Pools (${bodiesQuery.data?.length ?? 0})`} />
            <Tab label={`Service Reports (${reportsQuery.data?.length ?? 0})`} />
            <Tab label="Schedule" />
          </Tabs>
        </Box>
      </Paper>
      {tab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 1.5,
            mt: 1.5,
          }}
        >
          <Section title="LOCATION INFORMATION">
            <Field label="Address" value={addressText || 'Not provided'} />
            <Field
              label="Additional information"
              value={address?.additional_information || 'Not provided'}
            />
            {editing ? (
              <TextField
                fullWidth
                label="Gate / access code"
                value={gateCode}
                onChange={(e) => setGateCode(e.target.value)}
                sx={{ mb: 1.5 }}
              />
            ) : (
              <Field label="Gate / access code" value={location?.gate_code || 'Not provided'} />
            )}
            {saveLocation.isError && (
              <Alert severity="error">The service location could not be saved.</Alert>
            )}
          </Section>
          <Section title="OPERATIONAL SUMMARY">
            <Field label="Pools" value={String(bodiesQuery.data?.length ?? 0)} />
            <Field label="Service reports" value={String(reportsQuery.data?.length ?? 0)} />
            <Typography color="text.secondary">
              Scheduling and treatment records continue to use the existing PoolAppy data.
            </Typography>
          </Section>
        </Box>
      )}
      {tab === 1 && (
        <PoolManager
          clientId={clientId}
          locationId={locationId}
          pools={bodiesQuery.data ?? []}
          onChanged={() => bodiesQuery.refetch()}
        />
      )}
      {tab === 2 && (
        <Subgrid
          ids={(reportsQuery.data ?? []).map((report) => report.id)}
          headings={['Date', 'Technician', 'Completed', 'Management notes']}
          rows={(reportsQuery.data ?? []).map((x) => [
            new Date(x.created_at).toLocaleDateString('en-GB'),
            x.user?.name ?? '—',
            x.completed_at ? 'Yes' : 'No',
            x.notes_for_management ?? '—',
          ])}
          empty="No service reports have been logged."
          onOpen={(reportId) => {
            void navigate(
              `/clients/${clientId}/service-locations/${locationId}/reports/${reportId}`,
            );
          }}
        />
      )}
      {tab === 3 && (
        <ScheduleManager
          clientId={clientId}
          locationId={locationId}
          assignments={assignmentsQuery.data ?? []}
          oneOff={oneOffQuery.data ?? []}
          upcoming={upcomingQuery.data ?? []}
          onChanged={async () => {
            await Promise.all([
              assignmentsQuery.refetch(),
              oneOffQuery.refetch(),
              upcomingQuery.refetch(),
            ]);
          }}
        />
      )}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete service location?</DialogTitle>
        <DialogContent>
          This permanently deletes the location and its related operational data.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            disabled={removeLocation.isPending}
            onClick={() => removeLocation.mutate()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper elevation={0} sx={{ ...paperSx, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="body2">{label}</Typography>
      <Typography sx={{ bgcolor: '#f3f2f1', p: 1 }}>{value}</Typography>
    </Box>
  );
}
function Subgrid({
  ids,
  headings,
  rows,
  empty,
  onOpen,
}: {
  ids: number[];
  headings: string[];
  rows: string[][];
  empty: string;
  onOpen: (id: number) => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
  const someSelected = ids.some((id) => selectedIds.has(id));
  const toggle = (id: number) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, overflow: 'hidden' }}>
      <Box sx={{ minHeight: 50 }} />
      <Divider />
      <TableContainer>
        <Table
          size="small"
          aria-label="Service reports"
          sx={{
            minWidth: 720,
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              height: 48,
              py: 1.1,
              borderBottom: '1px solid #e1dfdd',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={!allSelected && someSelected}
                  onChange={() => setSelectedIds(allSelected ? new Set() : new Set(ids))}
                  inputProps={{ 'aria-label': 'Select all service reports' }}
                />
              </TableCell>
              {headings.map((heading) => (
                <TableCell key={heading} sx={{ fontWeight: 600 }}>
                  {heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length && (
              <TableRow>
                <TableCell
                  colSpan={headings.length + 1}
                  align="center"
                  sx={{ height: 150, color: 'text.secondary' }}
                >
                  {empty}
                </TableCell>
              </TableRow>
            )}
            {rows.map((row, index) => {
              const id = ids[index];
              if (id === undefined) return null;
              return (
                <TableRow
                  key={id}
                  hover
                  selected={selectedIds.has(id)}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open service report ${row[0] ?? id}`}
                  onClick={() => onOpen(id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpen(id);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: -2,
                    },
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedIds.has(id)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => toggle(id)}
                      inputProps={{ 'aria-label': `Select service report ${id}` }}
                    />
                  </TableCell>
                  {row.map((value, cellIndex) => (
                    <TableCell key={cellIndex}>{value}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Typography variant="body2" sx={{ px: 2, py: 1.25 }}>
        Rows: {rows.length}
      </Typography>
    </Paper>
  );
}
const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
